import React, { useMemo } from 'react';
import * as Sentry from "@sentry/react";
import { TextField, FormLabel, Box, FormGroup, Alert, Typography, Paper } from '@mui/material';
import APIRequest, { APIError, enqueueAPIErrorSnackbar } from "../../system/APIRequest";
import { DashboardDocument } from "components/resources/dashboards/DashboardDocument";
import { useState } from "react";
import { useTranslation } from 'react-i18next';
import SimpleFormDialog from 'components/dialogs/SimpleFormDialog';
import { DashboardPageInterface } from 'routes/dashboard/DashboardPage';
import DashboardSelectionButton from 'components/resources/dashboards/DashboardSelectionButton';
import { useSnackbar } from "notistack";
import { useNavigate } from 'react-router-dom';
import { getDashboardNavigatePath, parsePageParams } from 'components/resources/dashboards/DashboardNavigationUtils';

interface DashboardPageCopyDialogOptions {
  dashboard: DashboardDocument;
  page: DashboardPageInterface;
  onClose: (newDashboardId?:string) => void;
}

export default function DashboardPageCopyDialog(props: DashboardPageCopyDialogOptions): React.JSX.Element {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { page, dashboard, onClose } = props;

  const [newPageTitle, setNewPageTitle] = useState<string>(`${page.name} (copy)`);
  const [destinationDashboardId, setDestinationDashboardId] = useState<string>(dashboard.id);
  const [selectedDashboard, setSelectedDashboard] = useState<DashboardDocument | undefined>();

  const hasSameColumnCount = useMemo(() => {
    return dashboard.data.fullColumnCount === selectedDashboard?.data.fullColumnCount
  }, [dashboard, selectedDashboard]);

  // function that updates an existing page via the API
  const copyPage = () => {
    APIRequest(`obj/dashboard-page/${page.id}/duplicate`, {}, {
      method: "POST"
    }).then((duplicateResponse: any) => {
      const newId = duplicateResponse.id;
      const newUUID = self.crypto.randomUUID();
      const body:any = {
        uuid: newUUID,
        name: newPageTitle
      };
      if (dashboard.id !== destinationDashboardId) {
        body.dashboard_id = destinationDashboardId;
      }
      APIRequest(`obj/dashboard-page/${newId}`, {}, {
        method: "PUT",
        "body": JSON.stringify(body)
      }).then((response: any) => {
        // We now need to add the page to the dashboard
        const dashboardData = props.dashboard?.data || {};
        if (!dashboardData.pages) {
          dashboardData.pages = [];
        }
        dashboardData.pages.push(newUUID);

        APIRequest(`obj/dashboard/${props.dashboard?.id}`, {}, {
          method: "PUT",
          "body": JSON.stringify({
            data: dashboardData
          })
        }).then((innerResponse: any) => {
          enqueueSnackbar(i18n.t("dashboard.edit-pages.page-copied"), { variant: "success" });
          if (dashboard.id !== selectedDashboard?.id) {
            const origin = window.location.origin;
            const dashboardPath = `/dashboard/${selectedDashboard?.id}/page/${newId}`;
            window.open(`${origin}${dashboardPath}`, '_blank', 'noopener, noreferrer');
          }
          onClose(newId);
          if (duplicateResponse.id) {
            // Get copied page id, generate new url w/ query, if query is present
            const pageParams = parsePageParams();
            pageParams.pageId = duplicateResponse.id;
            const navigatePath = getDashboardNavigatePath(pageParams);
            window.location.href = navigatePath;
          }
        }).catch((error: APIError) => {
          enqueueAPIErrorSnackbar(error, enqueueSnackbar);
          Sentry.captureException(error);
        });
      });
    }).catch((error: APIError) => {
      enqueueAPIErrorSnackbar(error, enqueueSnackbar);
    });
  }

  return (
    <SimpleFormDialog
      isOpen={true}
      title={i18n.t("dashboard.edit-pages.copy")}
      onCancel={() => {
        onClose();
      }}
      onConfirm={() => {
        copyPage();
      }}
      confirmText={i18n.t("dashboard.edit-pages.copy")}
    >
      <Paper sx={{ p: 2 }}>
      <FormLabel>{i18n.t("dashboard.edit-pages.new-page-title")}:</FormLabel>
      <TextField
        fullWidth
        name="title"
        autoComplete="off"
        placeholder={i18n.t("dashboard.edit-pages.page-title-placeholder")}
        onChange={(event) => setNewPageTitle(event.target.value)}
        value={newPageTitle}
        variant="outlined"
      />
      <FormGroup sx={{mt:2}}>
        <FormLabel>{i18n.t("dashboard.edit-pages.destination-dashboard")}:</FormLabel>
        <Box>
          <DashboardSelectionButton
            selectedDashboardId={destinationDashboardId}
            setSelectedDashboardId={(dashboardId:string | undefined, dashboard) => {
              if (dashboardId) {
                setDestinationDashboardId(dashboardId);
              }
              setSelectedDashboard(dashboard);
            }}
          />
        </Box>
      </FormGroup>
      {!hasSameColumnCount &&
        <Alert color="warning" sx={{ marginTop: 1 }}>
          <Typography>{i18n.t("dashboard.edit-pages.column-mismatch-warning")}</Typography>
        </Alert>
      }
      </Paper>
    </SimpleFormDialog>
  );
}
